import client from '@common/utils/rest';
import { CellUpdatePayload, LoadISSFormatResponseDto } from '@modules/spreadsheets/dto/spreadsheet/type';

const URL_PREFIX = "api/spreadSheets/";

export const fetchSpreadsheetColumns = (formatId: number) => {
    return client({
        path: `${URL_PREFIX}loadISSFormat?formatId=${formatId}`,
        method: 'GET',
    }).then((response: any) => response.entity.data || response);
};

export const loadIndividualSpreadsheetFormat = (formatId: number) => {
    return client({
        path: `${URL_PREFIX}loadISSFormat?formatId=${formatId}`,
        method: 'GET',
    }).then((response: { entity: { data?: LoadISSFormatResponseDto } }) => {
        return response.entity.data || response;
    }).catch((error: Error) => {
        throw error;
    });
};

export const loadPedigreeSpreadsheetFormat = (formatId: number) => {
    return client({
        path: `${URL_PREFIX}loadPSSFormat?formatId=${formatId}`,
        method: 'GET',
    }).then((response: { entity: { data?: LoadISSFormatResponseDto } }) => {
        return response.entity.data || response;
    }).catch((error: Error) => {
        throw error;
    });
};

export const fetchSpreadsheetData = (body = {}) => {
    return client({
        path: URL_PREFIX + 'loadISS',
        method: 'POST',
        entity: body,
    }).then((response: any) => {
        if (response.entity.code !== 200) {
            throw new Error(response.entity.errors.join(', '));
        }
        return response.entity.data ;
    });
};

export const fetchPedigreeSpreadsheetData = (body = {}) => {
    return client({
        path: URL_PREFIX + 'loadPSS',
        method: 'POST',
        entity: body,
    }).then((response: any) => response.entity.data || response);
};

export const fetchSpreadsheetCount = (body = {}) => {
    return client({
        path: URL_PREFIX + 'member/count',
        method: 'POST',
        entity: body,
    }).then((response: any) => response.entity.data || response);
};

export const saveSSColWidth = (body: { context: number; colName: string; width: number }) => {
    return client({
        path: `${URL_PREFIX}columns/width?context=${body.context}&columnName=${body.colName}&width=${body.width}`,
        method: 'POST',
    }).then((response: any) => response.entity.data || response);
};

export const addSSRow = (body: { pss: boolean, rrn: number, column: number, globalId: number, columnModel: any }) => {
    return client({
        path: `${URL_PREFIX}row/add`,
        method: 'POST',
        entity: body,
    }).then((response: any) => response.entity.data || response);
};

export const deleteSSRow = (body: { pss: boolean, rrn: number, column: number, columnModel: any }) => {
    return client({
        path: `${URL_PREFIX}row/delete`,
        method: 'POST',
        entity: body,
    }).then((response: any) => response.entity || response);
};

export const updateCellValue = (payload: CellUpdatePayload): Promise<void> => {
    return client({
        path: "api/fields/updateValue",
        method: "POST",
        entity: payload,
    }).then((response: { entity: CellUpdatePayload }) => response.entity);
};

export const openDocument = (fileName: string, fieldId: number, fieldContext: number, objectId: number, tableId: number, rrn: number): void => {
    const params = new URLSearchParams({
        fieldId: fieldId.toString(),
        fieldContext: fieldContext.toString(),
        objectId: objectId.toString(),
        tableId: tableId.toString(),
        rrn: rrn.toString()
    });
    const url = `api/documents/open/${encodeURIComponent(fileName)}?${params.toString()}`;
    window.open(url, '_blank');
};

export const downloadDocument = (fileName: string, fieldId: number, fieldContext: number, objectId: number, tableId: number, rrn: number): void => {
    const params = new URLSearchParams({
        fieldId: fieldId.toString(),
        fieldContext: fieldContext.toString(),
        objectId: objectId.toString(),
        tableId: tableId.toString(),
        rrn: rrn.toString()
    });
    const url = `api/documents/download/${encodeURIComponent(fileName)}?${params.toString()}`;
    window.open(url, '_blank');
};

export const renameDocument = (
    oldFilename: string,
    newFilename: string,
    fieldId: number,
    fieldContext: number,
    objectId: number,
    tableId: number,
    rrn: number
): Promise<void> => {
    const params = new URLSearchParams({
        oldFilename,
        newFilename,
        fieldId: fieldId.toString(),
        fieldContext: fieldContext.toString(),
        objectId: objectId.toString(),
        tableId: tableId.toString(),
        rrn: rrn.toString(),
    });
    return fetch(`api/documents/rename?${params.toString()}`, { method: 'POST' })
        .then(async (res) => {
            if (!res.ok) {
                let message = `Rename failed: ${res.status} ${res.statusText}`;
                try {
                    const body = await res.json();
                    if (body && body.errors && body.errors.length) {
                        message = body.errors.join(', ');
                    }
                } catch {}
                const err: any = new Error(message);
                err.httpStatus = res.status;
                throw err;
            }
        });
};

export const uploadDocument = (
    file: File,
    fieldId: number,
    fieldContext: number,
    objectId: number,
    tableId: number,
    rrn: number,
    options: {
        overwrite?: boolean;
        isImage?: boolean;
        isProfilePhoto?: boolean;
    } = {}
): Promise<any> => {
    const formData = new FormData();

    // Add file to form data
    formData.append('file', file);

    // Add parameters
    formData.append('fieldId', fieldId.toString());
    formData.append('fieldContext', fieldContext.toString());
    formData.append('objectId', objectId.toString());
    formData.append('tableId', tableId.toString());
    formData.append('rrn', rrn.toString());
    formData.append('overwrite', (options.overwrite || false).toString());
    formData.append('isImage', (options.isImage || false).toString());
    formData.append('isProfilePhoto', (options.isProfilePhoto || false).toString());

    return fetch('api/documents/upload', {
        method: 'POST',
        body: formData,
    }).then(async response => {
        console.log("Upload API response status:", response.status);
        let responseData: any = {};
        try {
            responseData = await response.json();
        } catch (e) {
            responseData = {};
        }

        // Prefer logical body code if present; default to HTTP status
        const bodyCode = typeof responseData.code === 'number' ? Number(responseData.code) : (response.ok ? 200 : response.status);
        const errorsArr = Array.isArray(responseData.errors) ? responseData.errors : [];

        // Throw for HTTP error or logical error (e.g., 409 conflict in body)
        if (!response.ok || bodyCode !== 200 || errorsArr.length > 0) {
            const message = errorsArr.length > 0
                ? errorsArr.join(', ')
                : (response.statusText || 'Upload failed');
            const err: any = new Error(message);
            err.code = bodyCode;
            err.httpStatus = response.status;
            err.body = responseData;
            throw err;
        }

        // Success - return the body
        return responseData;
    });
};
